import { useCallback, useEffect, useState } from 'react';
import { loadAppData, logout as apiLogout, type AppData } from '../api/client';

export type SessionStatus = 'loading' | 'signed-out' | 'signed-in' | 'unreachable';

export interface Session {
	status: SessionStatus;
	data: AppData | null;
	error: string;
	/** Called after a successful login to adopt the new session. */
	adopt: (data: AppData) => void;
	signOut: () => Promise<void>;
	retry: () => void;
}

/**
 * Owns "is there a session".
 *
 * There is no client-side token to keep: the session is a SameSite=Strict
 * cookie the browser holds and we never see. Asking the server is the only way
 * to know, so a reload always starts here rather than trusting local state.
 */
export function useSession(): Session {
	const [status, setStatus] = useState<SessionStatus>('loading');
	const [data, setData] = useState<AppData | null>(null);
	const [error, setError] = useState('');
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;

		setStatus('loading');
		loadAppData()
			.then(appData => {
				if (cancelled) {
					return;
				}
				setData(appData);
				setStatus(appData.Auth ? 'signed-in' : 'signed-out');
			})
			.catch((e: unknown) => {
				if (cancelled) {
					return;
				}
				// A failure here is the backend being unreachable, not a rejected
				// login -- showing a login form would be a lie about what went wrong.
				setError(e instanceof Error ? e.message : String(e));
				setStatus('unreachable');
			});

		return () => {
			cancelled = true;
		};
	}, [attempt]);

	const adopt = useCallback((appData: AppData) => {
		setData(appData);
		setStatus(appData.Auth ? 'signed-in' : 'signed-out');
	}, []);

	const signOut = useCallback(async () => {
		try {
			await apiLogout();
		} catch {
			// Sign-out must always succeed locally. If the call failed the cookie
			// may survive, but leaving someone stuck in a session they asked to
			// leave is worse than a stale cookie the next request will reject.
		}
		setData(null);
		setStatus('signed-out');
	}, []);

	const retry = useCallback(() => setAttempt(value => value + 1), []);

	return { status, data, error, adopt, signOut, retry };
}
